import { ProseMirror } from "@progress/kendo-react-editor";
import { handleEditorError } from "../hooks";

const { Plugin, PluginKey } = ProseMirror;

export interface OrderedListStylePluginOptions {
  /** Whether to enable automatic styling on document changes */
  enableAutoStyling?: boolean;
  /** Custom style types for each nesting level (cycles through array) */
  styleTypes?: string[];
  /** Whether to preserve existing styles if they match expected pattern */
  preserveExistingStyles?: boolean;
}

/**
 * ProseMirror Ordered List Style Plugin for Kendo Editor
 * Automatically applies cycling list-style-type to nested ordered lists
 * Cycles: decimal → lower-alpha → lower-roman → decimal...
 */

// Default styling cycle: decimal → lower-alpha → lower-roman → repeat
const DEFAULT_STYLE_TYPES = ["decimal", "lower-alpha", "lower-roman"];

const DEFAULT_OPTIONS: Required<OrderedListStylePluginOptions> = {
  enableAutoStyling: true,
  styleTypes: DEFAULT_STYLE_TYPES,
  preserveExistingStyles: true
};

export const orderedListStylePluginKey = new PluginKey("orderedListStyle");

// Export style types for plugin manager
export { DEFAULT_STYLE_TYPES };

/**
 * Gets the appropriate list style type for a given nesting level
 */
const getStyleForLevel = (level: number, styleTypes: string[]): string => {
  const cyclePosition = (level - 1) % styleTypes.length;
  return styleTypes[cyclePosition];
};

/**
 * Parses existing inline styles from a style attribute
 */
const parseInlineStyles = (styleAttr: string): Record<string, string> => {
  const styles: Record<string, string> = {};

  if (!styleAttr) return styles;

  try {
    // Split by semicolon and parse each style declaration
    const declarations = styleAttr.split(";").filter(decl => decl.trim());

    declarations.forEach(declaration => {
      const [property, value] = declaration.split(":").map(part => part.trim());
      if (property && value) {
        styles[property] = value;
      }
    });
  } catch (error) {
    handleEditorError(error as Error, "parsing inline styles");
  }

  return styles;
};

/**
 * Converts style object back to inline style string
 */
const stylesToString = (styles: Record<string, string>): string => {
  return (
    Object.entries(styles)
      .map(([property, value]) => `${property}: ${value}`)
      .join("; ") + (Object.keys(styles).length > 0 ? ";" : "")
  );
};

/**
 * Calculates the nesting level of an ordered list node
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const calculateNestingLevel = (pos: any, doc: any): number => {
  try {
    const $pos = doc.resolve(pos);
    let level = 1; // Start at level 1 for the current list

    // Walk up the document tree to count parent ordered_list nodes
    // Start from depth - 1 to exclude the current node itself
    for (let i = $pos.depth - 1; i > 0; i--) {
      const node = $pos.node(i);
      if (node.type.name === "ordered_list") {
        level++; // Each parent ol increases the nesting level
      }
    }

    return level;
  } catch (error) {
    handleEditorError(error as Error, "calculating nesting level");
    return 1; // Fallback to level 1
  }
};

export const createOrderedListStylePlugin = (
  options: OrderedListStylePluginOptions = {}
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const processOrderedListStyling = (newState: any) => {
    try {
      if (!opts.enableAutoStyling) {
        return null;
      }

      const tr = newState.tr;
      let hasChanges = false;

      // Find all ordered_list nodes in the document
      newState.doc.descendants(
        (
          node: { type: { name: string }; attrs: { style: string } },
          pos: any
        ) => {
          try {
            // Only process ordered_list nodes
            if (node.type.name !== "ordered_list") {
              return true; // Continue traversing
            }

            // Calculate the nesting level for this ordered list
            const nestingLevel = calculateNestingLevel(pos, newState.doc);
            const expectedStyle = getStyleForLevel(
              nestingLevel,
              opts.styleTypes
            );

            // Get current style attribute
            const currentStyle = node.attrs.style || "";
            const currentStyles = parseInlineStyles(currentStyle);

            // Check if we need to update the list-style-type
            const currentListStyle = currentStyles["list-style-type"];

            if (
              !opts.preserveExistingStyles ||
              currentListStyle !== expectedStyle
            ) {
              // Update the list-style-type
              currentStyles["list-style-type"] = expectedStyle;

              // Create new style string
              const newStyleString = stylesToString(currentStyles);

              // Apply the style update through a transaction
              const newAttrs = { ...node.attrs, style: newStyleString };
              tr.setNodeMarkup(pos, null, newAttrs);
              hasChanges = true;
            }

            return true; // Continue traversing
          } catch (error) {
            handleEditorError(
              error as Error,
              `processing ordered list at pos ${pos}`
            );
            return true; // Continue traversing even if this node fails
          }
        }
      );

      if (hasChanges) {
        tr.setMeta("orderedListStyle-applied", true);
        return tr;
      }

      return null;
    } catch (error) {
      handleEditorError(error as Error, "ordered list styling processing");
      return null;
    }
  };

  return new Plugin({
    key: orderedListStylePluginKey,

    appendTransaction(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transactions: readonly any[],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      oldState: any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      newState: any
    ) {
      try {
        // Only process if there was user input that changed the document
        const hasUserInput = transactions.some(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (tr: any) =>
            tr.docChanged &&
            !tr.getMeta("orderedListStyle-applied") &&
            !tr.getMeta("autolink-applied") // Don't interfere with autolink
        );

        if (!hasUserInput) {
          return null;
        }

        // Check if any ordered lists were added, removed, or restructured
        let shouldProcess = false;

        // Simple heuristic: if the document structure changed involving lists
        transactions.forEach(tr => {
          if (tr.steps && tr.steps.length > 0) {
            tr.steps.forEach(
              (step: { slice: { content: { toString: () => any } } }) => {
                try {
                  // Check if the step involves list-related changes
                  if (step.slice && step.slice.content) {
                    const stepContent = step.slice.content.toString();
                    if (
                      stepContent.includes("ordered_list") ||
                      stepContent.includes("list_item")
                    ) {
                      shouldProcess = true;
                    }
                  }
                } catch (error) {
                  // If we can't analyze the step, process styling to be safe
                  shouldProcess = true;
                }
              }
            );
          }
        });

        // Also check if any ordered_list nodes exist in the new state
        if (!shouldProcess) {
          let hasOrderedLists = false;
          newState.doc.descendants((node: { type: { name: string } }) => {
            if (node.type.name === "ordered_list") {
              hasOrderedLists = true;
              return false; // Stop traversing
            }
            return true;
          });
          shouldProcess = hasOrderedLists;
        }

        if (shouldProcess) {
          return processOrderedListStyling(newState);
        }

        return null;
      } catch (error) {
        handleEditorError(
          error as Error,
          "ordered list style plugin appendTransaction"
        );
        return null;
      }
    },

    // Add view update for immediate styling on mount/load
    view(editorView) {
      try {
        // Apply initial styling when the view is created
        setTimeout(() => {
          try {
            const state = editorView.state;
            const transaction = processOrderedListStyling(state);

            if (transaction) {
              editorView.dispatch(transaction);
            }
          } catch (error) {
            handleEditorError(error as Error, "initial ordered list styling");
          }
        }, 100); // Small delay to ensure editor is fully initialized

        return {
          destroy: () => {
            // Cleanup if needed
          }
        };
      } catch (error) {
        handleEditorError(
          error as Error,
          "ordered list style plugin view initialization"
        );
        return {};
      }
    }
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isOrderedListStylePluginActive = (state: any): boolean => {
  return orderedListStylePluginKey.getState(state) !== undefined;
};
